import React from "react";
import { useDeal } from "../lib/store";
import { Card, RecBadge, TamFunnel } from "../components/shared";
import { fmt$ } from "../lib/model";
import { Radar } from "lucide-react";
import * as m from "../lib/model";

export default function AIEvaluation() {
  const { M, market: mktRaw, marketLoading: mktLoading, marketStale, runMarket, runAll } = useDeal();
  const mkt = marketStale ? null : mktRaw;
  const v = m.verdict(M);
  const dscr = m.dscrYr1(M);
  const quality = m.sdeQuality(M);
  const addbackPct = 100 - quality;
  const { declinePct, growing } = m.revenueTrend(M);
  const mult = m.nac(M) / M.sde;

  const hy = M.historicalYears || [], mix = M.revenueMix || [];
  const strengths = [
    hy.length ? ["Consistent top-line growth", `Revenue climbed from ${m.fmt$(hy[0].revenue)} to ${m.fmt$(M.ttmRevenue)} over the review period, signalling durable demand.`] : null,
    mix.length ? ["Recurring revenue base", `${mix[0].value}% of revenue comes from ${(mix[0].label || "").toLowerCase()}, providing predictable, contracted cash flow.`] : null,
    ["Attractive entry multiple", `Net acquisition cost implies a ${m.fmtX(mult)} multiple on SDE — below typical market comps for the sector.`],
    ["Seller financing in place", `A ${m.fmt$(M.snAmt)} seller note aligns the seller with a smooth transition and reduces senior leverage.`],
    ["Established operating history", `Founded in ${M.yearEstablished} with ${M.employeeCount} employees — a proven, staffed platform, not a startup.`],
    m.grossMargin(M) > 0 ? ["Healthy gross margins", `Gross margin of ${m.fmtPct(m.grossMargin(M))} leaves room to absorb owner-salary normalisation.`] : null,
  ].filter(Boolean);

  const risks = [
    ["SDE quality concentration", `${addbackPct.toFixed(0)}% of stated SDE is owner add-backs; true operating earnings after a market salary are ${m.fmt$(m.trueEarnings(M))}.`],
    ["Debt service coverage", `Year 1 DSCR of ${m.fmtX(dscr)} ${dscr < 1.3 ? "sits below the 1.30× comfort threshold" : "is comfortable"}, sensitive to owner-salary assumptions.`],
    ["Owner dependency", `Key-person risk is elevated in a founder-run business of this size; retention of crews and customers is critical.`],
    ["Customer concentration", `Diligence must confirm no single customer exceeds a prudent share of the ${m.fmt$(M.ttmRevenue)} top line.`],
    ["Working capital & equipment", `FF&E of ${m.fmt$(M.ffe)} may require near-term replacement CapEx beyond the modelled ${m.fmt$(M.capex)}/yr.`],
    ["Transition risk", `Reason for sale is "${M.reasonForSale}"; a structured consulting / non-compete period is essential.`],
  ];

  const register = [
    { lvl: "HIGH", t: "SDE add-back verification", d: "A large share of SDE is discretionary owner compensation and perks.", mit: "Commission an independent quality-of-earnings review; obtain payroll records and tax returns." },
    { lvl: dscr < 1.3 ? "HIGH" : "MED", t: "Debt service coverage", d: `Year 1 DSCR of ${m.fmtX(dscr)} leaves limited headroom against operating volatility.`, mit: "Structure a lower owner salary in year 1, negotiate seller-note standby, or increase equity." },
    { lvl: "MED", t: "Key-person / owner dependency", d: "Business relationships and estimating expertise concentrated in the departing owner.", mit: "3–5 year consulting agreement + earnout tied to revenue retention milestones." },
    { lvl: "MED", t: "Customer concentration", d: "Commercial maintenance contracts may be concentrated in a few accounts.", mit: "Obtain signed contracts and a customer-level revenue schedule; add reps & warranties." },
    { lvl: "LOW", t: "Equipment condition & CapEx", d: "Fleet and equipment age could drive maintenance CapEx above model.", mit: "Independent equipment appraisal; hold back a CapEx reserve at close." },
  ];

  return (
    <div className="page" data-testid="page-evaluation">
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div className="page-title">AI Evaluation</div>
          <div className="page-sub">Automated underwriting verdict & risk register</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button className="btn btn-primary btn-sm" onClick={runAll} data-testid="run-all-ai"><Radar size={12} style={{ marginRight: 4 }} />Run All AI</button>
          <RecBadge verdict={v} testid="eval-verdict" />
        </div>
      </div>

      <div className={`verdict-why ${v === "GO" ? "go" : v === "PASS" ? "pass" : "amber"}`} data-testid="verdict-reasons">
        <span className="vw-lbl">Why {v}:</span>
        {m.verdictReasons(M).map((r, i) => <span className="vw-chip" key={i}>{r}</span>)}
      </div>

      <div className="banner info" data-testid="source-attribution">
        <span>ℹ</span>
        <span>Analysis based on: uploaded CIM (if any), model assumptions, and historical financials for {M.companyName}. Verdict recomputes live as assumptions change.</span>
      </div>

      <Card title="Executive Summary" testid="exec-summary">
        <p className="bullet">
          <b>{M.companyName}</b> is a {M.dealType.toLowerCase()} opportunity located in {M.address}, established in {M.yearEstablished} with {M.employeeCount} employees. The business generates {m.fmt$(M.ttmRevenue)} in TTM revenue at a {m.fmtPct(M.grossMarginPct)} gross margin, with stated adjusted SDE of {m.fmt$(M.sde)}.
        </p>
        <p className="bullet">
          The deal is structured at an asking price of {m.fmt$(M.askPrice)} ({m.fmtX(mult)} on SDE{M.slbOn ? " net of sale-leaseback proceeds" : ""}), funded with a {M.downPct}% down payment, a {m.fmt$(m.loanAmt(M))} senior/SBA loan{M.snAmt > 0 ? `, and a ${m.fmt$(M.snAmt)} seller note` : ""}. {M.slbOn ? `Real estate is flipped to an investor at close, reducing the effective acquisition cost to ${m.fmt$(m.nac(M))}.` : ""}
        </p>
        <p className="bullet">
          The central thesis {v === "GO" ? "is favourable" : v === "PASS" ? "is challenged" : "is conditional"}: revenue is {growing ? "stable/growing" : `down ${declinePct.toFixed(0)}% from peak`}, but the deal's viability hinges on new-owner salary — true operating earnings after a market salary are {m.fmt$(m.trueEarnings(M))}, driving a Year 1 DSCR of {m.fmtX(dscr)}. {addbackPct > 60 ? "The heavy add-back profile is the primary underwriting risk." : "Add-back quality is within acceptable range."}
        </p>
      </Card>

      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="Strengths" testid="strengths-card"><div className="side-card green">{strengths.map(([h, d]) => <div className="bullet" key={h}><b>{h}.</b> {d}</div>)}</div></Card>
        <Card title="Risks" testid="risks-card"><div className="side-card red">{risks.map(([h, d]) => <div className="bullet" key={h}><b>{h}.</b> {d}</div>)}</div></Card>
      </div>

      <Card title="Risk Register" testid="risk-register" style={{ marginTop: 12 }}>
        {register.map((r) => (
          <div className="risk-row" key={r.t}>
            <div><span className={`pill ${r.lvl}`}>{r.lvl}</span></div>
            <div>
              <div className="title">{r.t}</div>
              <div className="desc">{r.d}</div>
              <div className="mit">↳ {r.mit}</div>
            </div>
          </div>
        ))}
      </Card>

      <Card
        title="Market Intelligence & Comps"
        testid="market-card"
        style={{ marginTop: 12 }}
        right={<button className="btn btn-primary" onClick={runMarket} disabled={mktLoading} data-testid="market-generate">{mktLoading ? "Analyzing…" : mkt ? "Refresh" : "Generate Market Comps"}</button>}
      >
        {!mkt && !mktLoading && (
          <div className="bullet">{marketStale ? "This deal changed since the last analysis — regenerate market intelligence so the comps, consolidators, roll-ups, exits and tailwinds / headwinds reflect this opportunity." : "Generate AI market intelligence for this deal's sector — comparable transaction multiples, the biggest consolidators active in the space, notable roll-ups, exits, and market tailwinds / headwinds."}</div>
        )}
        {mktLoading && <div className="bullet">Researching {M.companyName}'s sector, comps, consolidators and exits…</div>}
        {mkt && (
          <div data-testid="market-content">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <span className="tag">Sector: {mkt.sector}</span>
              {mkt.typicalMultiples?.range && <span className="tag">Typical: {mkt.typicalMultiples.range}</span>}
            </div>
            <p className="bullet">{mkt.marketOverview}</p>
            {mkt.tam && (
              <div data-testid="market-tam" style={{ margin: "10px 0" }}>
                <div className="section-label">Total Addressable Market (TAM → SAM → SOM)</div>
                <TamFunnel tam={mkt.tam} />
                {mkt.tam.note && <div className="callout" style={{ marginTop: 6 }}>{mkt.tam.note}</div>}
                {Array.isArray(mkt.tamCitations) && mkt.tamCitations.length > 0 && (
                  <div style={{ marginTop: 6 }} data-testid="tam-sources">
                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text3)", marginBottom: 3 }}>TAM sources</div>
                    {mkt.tamCitations.slice(0, 4).map((c, i) => <a key={i} href={c.url} target="_blank" rel="noreferrer" style={{ display: "block", fontSize: 11, color: "var(--text2)" }}>{i + 1}. {c.title}</a>)}
                  </div>
                )}
              </div>
            )}
            {mkt.typicalMultiples?.note && <div className="callout">{mkt.typicalMultiples.note}</div>}

            {Array.isArray(mkt.comps) && mkt.comps.length > 0 && (
              <>
                <div className="section-label">Comparable transactions{Array.isArray(mkt.compCitations) && mkt.compCitations.length ? " (live, cited)" : " (illustrative)"}</div>
                <table className="tbl comps-tbl" data-testid="comps-table">
                  <thead><tr><th>Business</th><th>Location</th><th className="num">Revenue</th><th className="num">Multiple</th><th className="num">Year</th></tr></thead>
                  <tbody>
                    {mkt.comps.map((c, i) => (
                      <tr key={i} data-testid={`comp-row-${i}`}><td><div className="comp-name">{c.descriptor}</div>{c.note && <div className="comp-note">{c.note}</div>}</td><td className="comp-loc">{c.location || "—"}</td><td className="num">{c.revenue ? fmt$(c.revenue) : "—"}</td><td className="num">{c.multiple || "—"}</td><td className="num">{c.year || "—"}</td></tr>
                    ))}
                  </tbody>
                </table>
                {Array.isArray(mkt.compCitations) && mkt.compCitations.length > 0 && (
                  <div style={{ marginTop: 6 }} data-testid="comp-sources">
                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text3)", marginBottom: 3 }}>Comp sources</div>
                    {mkt.compCitations.slice(0, 4).map((c, i) => <a key={i} href={c.url} target="_blank" rel="noreferrer" style={{ display: "block", fontSize: 11, color: "var(--text2)" }}>{i + 1}. {c.title}</a>)}
                  </div>
                )}
              </>
            )}

            <div className="grid g2" style={{ marginTop: 4 }}>
              {Array.isArray(mkt.consolidators) && mkt.consolidators.length > 0 && (
                <div>
                  <div className="section-label">Active consolidators / acquirers{Array.isArray(mkt.consolidatorCitations) && mkt.consolidatorCitations.length ? " (live, cited)" : " (illustrative)"}</div>
                  <div className="cons-list" data-testid="consolidators-list">
                    {mkt.consolidators.map((c, i) => (
                      <div className="cons-row" key={i} data-testid={`consolidator-${i}`}>
                        <div className="cons-head"><b>{c.name}</b>{c.type && <span className="tag">{c.type}</span>}{c.sponsor && c.sponsor !== "null" && <span className="ra-sub">backed by {c.sponsor}</span>}{c.hq && c.hq !== "null" && <span className="ra-sub">· {c.hq}</span>}</div>
                        {c.recentDeals && <div className="cons-line"><span className="k">Recent</span>{c.recentDeals}</div>}
                        {c.appetite && <div className="cons-line"><span className="k">Appetite</span>{c.appetite}</div>}
                        {c.note && <div className="cons-line ra-sub">{c.note}</div>}
                      </div>
                    ))}
                  </div>
                  {Array.isArray(mkt.consolidatorCitations) && mkt.consolidatorCitations.length > 0 && (
                    <div style={{ marginTop: 6 }} data-testid="consolidator-sources">
                      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text3)", marginBottom: 3 }}>Consolidator sources</div>
                      {mkt.consolidatorCitations.slice(0, 5).map((c, i) => <a key={i} href={c.url} target="_blank" rel="noreferrer" style={{ display: "block", fontSize: 11, color: "var(--text2)" }}>{i + 1}. {c.title}</a>)}
                    </div>
                  )}
                </div>
              )}
              {Array.isArray(mkt.rollups) && mkt.rollups.length > 0 && (
                <div>
                  <div className="section-label">Notable roll-ups</div>
                  {mkt.rollups.map((r, i) => <div className="bullet" key={i}><b>{r.name}.</b> {r.description}{r.outcome ? ` — ${r.outcome}` : ""}</div>)}
                </div>
              )}
            </div>

            {Array.isArray(mkt.exits) && mkt.exits.length > 0 && (
              <>
                <div className="section-label">Notable exits</div>
                <div className="table-scroll">
                  <table className="tbl">
                    <thead><tr><th>Target</th><th>Acquirer</th><th className="num">Multiple</th><th className="num">Year</th></tr></thead>
                    <tbody>
                      {mkt.exits.map((e, i) => <tr key={i}><td>{e.target}</td><td>{e.acquirer}</td><td className="num">{e.multiple || "—"}</td><td className="num">{e.year || "—"}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="grid g2" style={{ marginTop: 4 }}>
              {Array.isArray(mkt.tailwinds) && mkt.tailwinds.length > 0 && (
                <div><div className="section-label">Tailwinds</div><div className="side-card green">{mkt.tailwinds.map((t, i) => <div className="bullet" key={i}>{t}</div>)}</div></div>
              )}
              {Array.isArray(mkt.headwinds) && mkt.headwinds.length > 0 && (
                <div><div className="section-label">Headwinds</div><div className="side-card red">{mkt.headwinds.map((t, i) => <div className="bullet" key={i}>{t}</div>)}</div></div>
              )}
            </div>
            <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 10 }}>AI-generated market context — illustrative, verify against primary sources before IC.</div>
          </div>
        )}
      </Card>
    </div>
  );
}
